import { makeRequest } from "../makeRequest";
import { PhoneNumberUtil } from "google-libphonenumber";
import axios from "axios";

export const encodeURL = (url) => {
  /*
    const snakeCase = url.toLowerCase().replace(/\s+/g, '-') // Replace spaces with underscores
    */
  return encodeURIComponent(url?.toLowerCase());
};

export const capitalizeWords = (str) => {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};
const safeToFixed = (value, digits = 2) =>
  (parseFloat(value) || 0).toFixed(digits);


export const calculatePromoDeduct = (promo, subtotal) => {
  if (promo?.attributes?.Type === "Percentage") {
    return safeToFixed(subtotal * (promo?.attributes?.Amount / 100));
  } else {
    return safeToFixed(promo?.attributes?.Amount);
  }
};

// Re-fetches the promo/voucher from the API and runs all validity checks.
// Returns the freshly fetched promo object when valid, or null when invalid.
export const getFreshPromo = async (user, promoParameter, isVoucher) => {
  // re-fetch the promo
  let promo = null;

  if (isVoucher) {
    // re-fetch voucher
    const promoBaseUrl = "/vouchers";
    const promoParams = new URLSearchParams({
      populate: "*",
      "filters[Code][$eq]": promoParameter?.attributes?.Code,
      // cache-bust so an admin change to the voucher is honored at order time
      _: Date.now(),
    });
    const promoUrl = `${promoBaseUrl}?${promoParams?.toString()}`;

    try {
      const res = await makeRequest.get(promoUrl);

      if (res?.data?.meta?.pagination?.total > 0) {
        promo = res?.data?.data[0];
      }
    } catch (err) {
      return null;
    }
  } else {
    // re-fetch promo
    const promoBaseUrl = "/promo-codes";
    const promoParams = new URLSearchParams({
      populate: "*",
      "filters[Name][$eq]": promoParameter?.attributes?.Name,
      // cache-bust so an admin change to the promo is honored at order time
      _: Date.now(),
    });
    const promoUrl = `${promoBaseUrl}?${promoParams?.toString()}`;

    try {
      const res = await makeRequest.get(promoUrl);

      if (res?.data?.meta?.pagination?.total > 0) {
        promo = res?.data?.data[0];
      }
    } catch (err) {
      return null;
    }
  }

  if (!promo) return null;

  // check promo expiry date
  const expiryDate = promo?.attributes?.ExpiryDate;

  if (expiryDate) {
    const today = new Date();
    const expiry = new Date(expiryDate);

    if (expiry < today) return null;
  }

  // check promo status
  if (!isVoucher && promo?.attributes?.Status !== true) return null;

  // check promo uses per customer
  if (!isVoucher && Object.keys(user).length > 0) {
    const promoBaseUrl = "/orders";
    const promoParams = new URLSearchParams({
      populate: "*",
      "filters[User][id][$eq]": user?.id,
      "filters[CouponCode][$eq]": promo?.attributes?.Name,
      // cache-bust so usage counts read the freshest order data at order time
      _: Date.now(),
    });
    const promoUrl = `${promoBaseUrl}?${promoParams?.toString()}`;

    try {
      const res = await makeRequest.get(promoUrl);

      if (
        res?.data?.meta?.pagination?.total >= promo?.attributes?.UsesPerCustomer
      ) {
        return null;
      }
    } catch (err) {
      return null;
    }
  }

  // check promo total uses
  if (!isVoucher) {
    const promoBaseUrl = "/orders";
    const promoParams = new URLSearchParams({
      populate: "*",
      "filters[CouponCode][$eq]": promo?.attributes?.Name,
      // cache-bust so usage counts read the freshest order data at order time
      _: Date.now(),
    });
    const promoUrl = `${promoBaseUrl}?${promoParams?.toString()}`;

    try {
      const res = await makeRequest.get(promoUrl);

      if (
        res?.data?.meta?.pagination?.total >= promo?.attributes?.TotalUsesNumber
      ) {
        return null;
      }
    } catch (err) {
      return null;
    }
  }

  // check if voucher active
  if (
    isVoucher &&
    promo?.attributes?.Status !== "Active"
    // promo?.attributes?.Status !== "Used"
  )
    return null;

  // check if voucher is for current user
  // if (isVoucher && promo?.attributes?.User?.data?.id !== user?.id) return false;

  return promo;
};

export const isPromoValid = async (user, promoParameter, isVoucher) => {
  const promo = await getFreshPromo(user, promoParameter, isVoucher);
  return !!promo;
};

export const normalizeDate = (date, isUTCTime = false) => {
  let normalized = new Date(date);
  return normalized;
};

export const isOfferCurrentlyValid = (offer) => {
  const now = new Date();
  const startDate = normalizeDate(offer?.attributes?.StartDate, true);
  const expiryDate = normalizeDate(offer?.attributes?.ExpiryDate, true);
  return now >= startDate && now <= expiryDate;
};

export const getCheapestProduct = (products) => {
  if (products?.length === 1) {
    return products[0];
  }

  let cheapestProduct = null;
  for (const product of products) {
    if (!cheapestProduct) {
      cheapestProduct = product;
      continue;
    }

    const cheapestProductPrice =
      cheapestProduct?.product?.attributes?.discountedPrice ??
      cheapestProduct?.product?.attributes?.Price;

    const productPrice =
      product?.product?.attributes?.discountedPrice ??
      product?.product?.attributes?.Price;

    if (cheapestProductPrice > productPrice) {
      cheapestProduct = product;
    }
  }

  return cheapestProduct;
};

export const getNCheapestProducts = (n, products) => {
  const sortedProducts = products.toSorted((a, b) => {
    const aPrice =
      a?.product?.attributes?.discountedPrice ?? a?.product?.attributes?.Price;
    const bPrice =
      b?.product?.attributes?.discountedPrice ?? b?.product?.attributes?.Price;

    return aPrice - bPrice;
  });
  const offeredProducts = [];
  let num = n;
  while (num > 0) {
    for (const product of sortedProducts) {
      if (product?.qty <= num) {
        offeredProducts.push({
          productID: product?.product?.id,
          quantity: product?.qty,
          barcode: product?.barcode,
        });
        num -= product?.qty;
      } else {
        if (num <= 0) break;

        offeredProducts.push({
          productID: product?.product?.id,
          quantity: num,
          barcode: product?.barcode,
        });
        num = 0;
        break;
      }
    }
  }
  return offeredProducts;
};

// export const applyDiscountRules = (
//   products,
//   discountRules,
//   variantId = null,
//   barcode = null,
//   selectedCountryID = 0
// ) => {
//   if (
//     !discountRules?.isLoading &&
//     !discountRules?.isError &&
//     products &&
//     products[0] &&
//     products?.length > 0 &&
//     Object.keys(products[0])?.length > 0
//   ) {
//     const rules = [...discountRules.rules].sort(
//       (a, b) => a?.attributes?.Priority - b.attributes?.Priority
//     );

//     for (const product of products) {
//       let newPrice = product?.attributes?.Price;

//       if (product?.attributes?.SpecialPrice) {
//         product.attributes.discountedPrice = product?.attributes?.SpecialPrice;
//         continue;
//       }

//       const categories = product?.attributes?.Categories?.data?.map(
//         (cat) => cat?.id
//       );
//       const tags = product?.attributes?.Tags?.data?.map((tag) => tag?.id);

//       for (const rule of rules) {
//         if (rule?.attributes?.FreeShipping) {
//           continue;
//         }

//         const today = normalizeDate(new Date());
//         const fromDate = normalizeDate(rule?.attributes?.FromDate, true);
//         const toDate = normalizeDate(rule?.attributes?.ToDate, true);

//         if (toDate < today || fromDate > today) {
//           continue;
//         }

//         if (rule?.attributes?.ExcludeProducts?.data?.length > 0) {
//           const isProductExcluded =
//             rule?.attributes?.ExcludeProducts?.data?.find(
//               (prod) => prod?.id === product?.id
//             );
//           if (isProductExcluded) {
//             continue;
//           }
//         }

//         if (rule?.attributes?.IncludeProducts?.data?.length > 0) {
//           const isProductIncluded =
//             rule?.attributes?.IncludeProducts?.data?.find(
//               (prod) => prod?.id === product?.id
//             );
//           if (!isProductIncluded) {
//             continue;
//           }
//         }

//         let amountToDeduct = 0;
//         // if (rule?.attributes?.Type === "Percentage") {
//         //   amountToDeduct = (newPrice * (rule?.attributes?.Value / 100)).toFixed(
//         //     2
//         //   );
//         // } else {
//         //   amountToDeduct = (rule?.attributes?.Value).toFixed(2);
//         // }
//         const ruleValue = parseFloat(rule?.attributes?.Value) || 0; 
//         if (rule?.attributes?.Type === "Percentage") {
//         const rawDeduct = newPrice * (ruleValue / 100);
//         amountToDeduct = (typeof rawDeduct === 'number' ? rawDeduct : 0).toFixed(2);
//         } else {
//         amountToDeduct = (typeof ruleValue === 'number' ? ruleValue : 0).toFixed(2);
//         }

//         const ruleFilters = {
//           category: false,
//           colors: false,
//           country: false,
//           sizes: false,
//           tags: false,
//         };
//         const matchingObject = {
//           category: false,
//           colors: false,
//           country: false,
//           sizes: false,
//           tags: false,
//         };

//         if (rule?.attributes?.categories?.data?.length > 0)
//           ruleFilters.category = true;
//         if (rule?.attributes?.colors?.data?.length > 0)
//           ruleFilters.colors = true;
//         if (rule?.attributes?.country?.data?.id) ruleFilters.country = true;
//         if (rule?.attributes?.sizes?.data?.length > 0) ruleFilters.sizes = true;
//         if (rule?.attributes?.tags?.data > 0) ruleFilters.tags = true;

//         if (ruleFilters.category) {
//           for (const category of rule?.attributes?.categories?.data) {
//             if (categories?.find((cat) => cat === category?.id)) {
//               matchingObject.category = true;
//               break;
//             }
//           }
//         }

//         if (ruleFilters.colors && variantId) {
//           const selectedVariant = product?.attributes?.Variants?.find(
//             (variant) => variant.id === variantId
//           );
//           if (selectedVariant) {
//             for (const color of rule?.attributes?.colors?.data) {
//               if (color?.id === selectedVariant?.Color?.data?.id) {
//                 matchingObject.colors = true;
//                 break;
//               }
//             }
//           }
//         }

//         if (ruleFilters.country && selectedCountryID) {
//           const countryDiscountId = rule?.attributes?.country?.data?.id ?? null;
//           if (countryDiscountId && countryDiscountId == selectedCountryID) {
//             matchingObject.country = true;
//           }
//         }

//         if (ruleFilters.sizes && barcode) {
//           const selectedVariant = product?.attributes?.Variants?.find(
//             (variant) => variant.id === variantId
//           );
//           if (selectedVariant) {
//             const selectedSize = selectedVariant?.Sizes?.find(
//               (size) => size?.Barcode === barcode
//             );
//             if (selectedSize) {
//               for (const size of rule?.attributes?.sizes?.data) {
//                 if (size?.id === selectedSize?.Size?.data?.id) {
//                   matchingObject.sizes = true;
//                   break;
//                 }
//               }
//             }
//           }
//         }

//         if (ruleFilters.tags) {
//           for (const tag of rule?.attributes?.tags?.data) {
//             if (tags.find((t) => t === tag?.id)) {
//               matchingObject.tags = true;
//               break;
//             }
//           }
//         }

//         // check if all filters are correct
//         if (
//           ruleFilters.category === matchingObject.category &&
//           ruleFilters.colors === matchingObject.colors &&
//           ruleFilters.country === matchingObject.country &&
//           ruleFilters.sizes === matchingObject.sizes &&
//           ruleFilters.tags === matchingObject.tags
//         ) {
//           newPrice -= amountToDeduct;
//           break;
//         }
//       }

//       if (newPrice === product?.attributes?.Price) {
//         product.attributes.discountedPrice = null;
//       } else {
//         product.attributes.discountedPrice = newPrice;
//       }
//     }
//   }
//   return products;
// };


export const applyDiscountRules = (
  products,
  discountRules,
  variantId = null,
  barcode = null,
  selectedCountryID = 0
) => {
  if (
    !discountRules?.isLoading &&
    !discountRules?.isError &&
    products &&
    products[0] &&
    products?.length > 0 &&
    Object.keys(products[0])?.length > 0
  ) {
    const rules = [...discountRules.rules].sort(
      (a, b) => a?.attributes?.Priority - b.attributes?.Priority
    );

    for (const product of products) {
      // siham
      if (!product || !product.attributes) continue;

      let newPrice = parseFloat(product?.attributes?.Price) || 0;

      if (product?.attributes?.SpecialPrice) {
        product.attributes.discountedPrice = safeToFixed(product?.attributes?.SpecialPrice);
        continue;
      }

      const categories = product?.attributes?.Categories?.data?.map(
        (cat) => cat?.id
      );
      const tags = product?.attributes?.Tags?.data?.map((tag) => tag?.id);

      for (const rule of rules) {
        if (rule?.attributes?.FreeShipping) {
          continue;
        }
        // Defensive: never apply a rule that is not active, even if it slipped
        // in from a stale/localStorage copy.
        if (!rule?.attributes?.Status) {
          continue;
        }

        const today = normalizeDate(new Date());
        const fromDate = normalizeDate(rule?.attributes?.FromDate, true);
        const toDate = normalizeDate(rule?.attributes?.ToDate, true);

        if (toDate < today || fromDate > today) {
          continue;
        }

        if (rule?.attributes?.ExcludeProducts?.data?.length > 0) {
          const isProductExcluded =
            rule?.attributes?.ExcludeProducts?.data?.find(
              (prod) => prod?.id === product?.id
            );
          if (isProductExcluded) {
            continue;
          }
        }

        if (rule?.attributes?.IncludeProducts?.data?.length > 0) {
          const isProductIncluded =
            rule?.attributes?.IncludeProducts?.data?.find(
              (prod) => prod?.id === product?.id
            );
          if (!isProductIncluded) {
            continue;
          }
        }

        const ruleValue = parseFloat(rule?.attributes?.Value) || 0;
        let amountToDeduct = 0;
        
        if (rule?.attributes?.Type === "Percentage") {
          amountToDeduct = newPrice * (ruleValue / 100);
        } else {
          amountToDeduct = ruleValue;
        }

        const ruleFilters = {
          category: false,
          colors: false,
          country: false,
          sizes: false,
          tags: false,
        };
        const matchingObject = {
          category: false,
          colors: false,
          country: false,
          sizes: false,
          tags: false,
        };

        if (rule?.attributes?.categories?.data?.length > 0)
          ruleFilters.category = true;
        if (rule?.attributes?.colors?.data?.length > 0)
          ruleFilters.colors = true;
        if (rule?.attributes?.country?.data?.id) ruleFilters.country = true;
        if (rule?.attributes?.sizes?.data?.length > 0) ruleFilters.sizes = true;
        if (rule?.attributes?.tags?.data > 0) ruleFilters.tags = true;

        if (ruleFilters.category) {
          for (const category of rule?.attributes?.categories?.data) {
            if (categories?.find((cat) => cat === category?.id)) {
              matchingObject.category = true;
              break;
            }
          }
        }

        if (ruleFilters.colors && variantId) {
          const selectedVariant = product?.attributes?.Variants?.find(
            (variant) => variant.id === variantId
          );
          if (selectedVariant) {
            for (const color of rule?.attributes?.colors?.data) {
              if (color?.id === selectedVariant?.Color?.data?.id) {
                matchingObject.colors = true;
                break;
              }
            }
          }
        }

        if (ruleFilters.country && selectedCountryID) {
          const countryDiscountId = rule?.attributes?.country?.data?.id ?? null;
          if (countryDiscountId && countryDiscountId == selectedCountryID) {
            matchingObject.country = true;
          }
        }

        if (ruleFilters.sizes && barcode) {
          const selectedVariant = product?.attributes?.Variants?.find(
            (variant) => variant.id === variantId
          );
          if (selectedVariant) {
            const selectedSize = selectedVariant?.Sizes?.find(
              (size) => size?.Barcode === barcode
            );
            if (selectedSize) {
              for (const size of rule?.attributes?.sizes?.data) {
                if (size?.id === selectedSize?.Size?.data?.id) {
                  matchingObject.sizes = true;
                  break;
                }
              }
            }
          }
        }

        if (ruleFilters.tags) {
          for (const tag of rule?.attributes?.tags?.data) {
            if (tags.find((t) => t === tag?.id)) {
              matchingObject.tags = true;
              break;
            }
          }
        }

        if (
          ruleFilters.category === matchingObject.category &&
          ruleFilters.colors === matchingObject.colors &&
          ruleFilters.country === matchingObject.country &&
          ruleFilters.sizes === matchingObject.sizes &&
          ruleFilters.tags === matchingObject.tags
        ) {
          newPrice -= amountToDeduct;
          break;
        }
      }

      //siham
      const originalPrice = parseFloat(product?.attributes?.Price) || 0;
      if (product.attributes) {
        if (newPrice >= originalPrice) {
          product.attributes.discountedPrice = null;
        } else {
          product.attributes.discountedPrice = newPrice.toFixed(2);
        }
      }
    }
  }
  return products;
};

export const calculateFreeShipping = (
  products,
  discountRules,
  subtotal,
  shippingProvider,
  selectedCountryID = 0
) => {
  // Free shipping is granted exclusively by active Strapi discount-rules
  // (Status + date window + country/minimum-subtotal conditions). No hardcoded
  // country/subtotal bypass — a disabled rule must never keep applying.

  if (
    !discountRules?.isLoading &&
    !discountRules?.isError &&
    products &&
    products?.length > 0
  ) {
    const rules = [...discountRules.rules].sort(
      (a, b) => a?.attributes?.Priority - b.attributes?.Priority
    );

    for (const rule of rules) {
      if (!rule?.attributes?.FreeShipping) {
        continue;
      }
      // Defensive: never grant free shipping from a rule that is not active,
      // even if it slipped in from a stale/localStorage copy.
      if (!rule?.attributes?.Status) {
        continue;
      }

      let hasFreeShipping = true;

      // NOTE: the FromDate/ToDate window is intentionally NOT checked here.
      // Free shipping is gated only by Status + FreeShipping (plus the
      // remaining filters below), so an expired date does not disable it.

      const ruleFilters = {
        category: false,
        colors: false,
        country: false,
        sizes: false,
        tags: false,
      };

      if (rule?.attributes?.categories?.data?.length > 0)
        ruleFilters.category = true;
      if (rule?.attributes?.colors?.data?.length > 0) ruleFilters.colors = true;
      if (rule?.attributes?.country?.data?.id) ruleFilters.country = true;
      if (rule?.attributes?.sizes?.data?.length > 0) ruleFilters.sizes = true;
      if (rule?.attributes?.tags?.data > 0) ruleFilters.tags = true;

      for (const product of products) {
        if (rule?.attributes?.ExcludeProducts?.data?.length > 0) {
          const isProductExcluded =
            rule?.attributes?.ExcludeProducts?.data?.find(
              (prod) => prod?.id === product?.product?.id
            );
          if (isProductExcluded) {
            hasFreeShipping = false;
            break;
          }
        }

        if (rule?.attributes?.IncludeProducts?.data?.length > 0) {
          const isProductIncluded =
            rule?.attributes?.IncludeProducts?.data?.find(
              (prod) => prod?.id === product?.product?.id
            );
          if (!isProductIncluded) {
            hasFreeShipping = false;
            break;
          }
        }

        const matchingObject = {
          category: false,
          colors: false,
          country: false,
          sizes: false,
          tags: false,
        };

        const categories = product?.product?.attributes?.Categories?.data?.map(
          (cat) => cat?.id
        );
        const tags = product?.product?.attributes?.Tags?.data?.map(
          (tag) => tag?.id
        );

        if (ruleFilters.category) {
          for (const category of rule?.attributes?.categories?.data) {
            if (categories?.find((cat) => cat === category?.id)) {
              matchingObject.category = true;
              break;
            }
          }
        }

        if (ruleFilters.colors) {
          const selectedVariant = product?.product?.attributes?.Variants?.find(
            (variant) => variant.id === product?.selectedVariantId
          );
          if (selectedVariant) {
            for (const color of rule?.attributes?.colors?.data) {
              if (color?.id === selectedVariant?.Color?.data?.id) {
                matchingObject.colors = true;
                break;
              }
            }
          }
        }

        if (ruleFilters.country && selectedCountryID) {
          const countryDiscountId = rule?.attributes?.country?.data?.id ?? null;
          if (countryDiscountId && countryDiscountId == selectedCountryID) {
            matchingObject.country = true;
          }
        }

        if (ruleFilters.sizes) {
          const selectedVariant = product?.product?.attributes?.Variants?.find(
            (variant) => variant.id === product?.selectedVariantId
          );
          if (selectedVariant) {
            const selectedSize = selectedVariant?.Sizes?.find(
              (size) => size?.Barcode === product?.barcode
            );
            if (selectedSize) {
              for (const size of rule?.attributes?.sizes?.data) {
                if (size?.id === selectedSize?.Size?.data?.id) {
                  matchingObject.sizes = true;
                  break;
                }
              }
            }
          }
        }

        if (ruleFilters.tags) {
          for (const tag of rule?.attributes?.tags?.data) {
            if (tags.find((t) => t === tag?.id)) {
              matchingObject.tags = true;
              break;
            }
          }
        }

        // check if any filters aren't correct
        if (
          ruleFilters.category !== matchingObject.category ||
          ruleFilters.colors !== matchingObject.colors ||
          ruleFilters.country !== matchingObject.country ||
          ruleFilters.sizes !== matchingObject.sizes ||
          ruleFilters.tags !== matchingObject.tags
        ) {
          hasFreeShipping = false;
          break;
        }
      }

      if (rule?.attributes?.MinimumSubtotal) {
        if (
          parseFloat(rule?.attributes?.MinimumSubtotal) > parseFloat(subtotal)
        )
          continue;
      }

      if (rule?.attributes?.ShippingProviders?.data?.length > 0) {
        let isShippingProviderIncluded = false;
        for (const ruleShippingProvider of rule?.attributes?.ShippingProviders
          ?.data) {
          if (shippingProvider == ruleShippingProvider?.attributes?.Name) {
            isShippingProviderIncluded = true;
            break;
          }
        }

        if (!isShippingProviderIncluded) continue;
      }

      if (hasFreeShipping) {
        return true;
      }
    }

    return false;
  }

  return false;
};

export const applyOffers = (products, offers) => {
  if (products?.length <= 0 || offers?.length <= 0) {
    return { offer: null, tip: "", offerAppliedProducts: [] };
  }

  let appliedOffer = null;
  let CheapestProducts = [];
  let offerTip = "";
  const sortedProducts = products?.toSorted((a, b) => {
    const aPrice =
      a?.product?.attributes?.discountedPrice ?? a?.product?.attributes?.Price;
    const bPrice =
      b?.product?.attributes?.discountedPrice ?? b?.product?.attributes?.Price;

    return bPrice - aPrice;
  });

  for (const offer of offers) {
    let isApplicable = true;

    const today = normalizeDate(new Date());
    const fromDate = normalizeDate(offer?.attributes?.StartDate, true);
    const toDate = normalizeDate(offer?.attributes?.ExpiryDate, true);

    if (toDate < today || fromDate > today) {
      continue;
    }

    if (
      !offer?.attributes?.Quantity ||
      !offer?.attributes?.OfferQuantity ||
      !offer?.attributes?.DiscountType ||
      !offer?.attributes?.DiscountValue
    ) {
      continue;
    }

    const offerFilters = {
      categories: offer?.attributes?.Categories?.data?.map((cat) => cat?.id),
      categoriesQuantity: offer?.attributes?.Quantity,
      offerCategories: offer?.attributes?.OfferCategories?.data?.map(
        (cat) => cat?.id
      ),
      offerCategoriesQuantity: offer?.attributes?.OfferQuantity,
      excludedCategories: offer?.attributes?.ExcludeOfferCategories?.data?.map(
        (cat) => cat?.id
      ),
      excludedProducts: offer?.attributes?.ExcludeOfferProducts?.data?.map(
        (prod) => prod?.id
      ),
    };

    const matchingObject = {
      categoriesQuantity: offerFilters?.categories?.map((cat) => {
        return { id: cat, qty: 0 };
      }),
      offerCategoriesQuantity: offerFilters?.offerCategories?.map((cat) => {
        return { id: cat, qty: 0 };
      }),
    };

    const remainingProducts = sortedProducts.map((product) => {
      if (product) {
        return {
          ...product,
          remainingQty: product?.qty,
        };
      }
    });

    const filteredProducts = [];
    if (offerFilters?.excludedProducts?.length === 0) {
      filteredProducts.push(...remainingProducts);
    } else {
      for (const prod of remainingProducts) {
        const productID = prod?.product?.id;
        const isProdIDInExcludedProducts = offerFilters?.excludedProducts?.find(
          (ep) => ep === productID
        );
        if (!isProdIDInExcludedProducts) {
          filteredProducts.push(prod);
        }
      }
    }

    const isCategoryDependent =
      offer?.attributes?.Categories?.data?.length === 0 ? false : true;
    const isOfferCategoryDependent =
      offer?.attributes?.OfferCategories?.data?.length === 0 ? false : true;

    let categoryProducts = [];
    let offerProducts = [];

    let isCategoryQuantityFull = false;
    let allCategoriesQtys = 0;
    let isOfferCategoryQuantityFull = false;
    let allOfferCategoriesQtys = 0;

    if (!isCategoryDependent && !isOfferCategoryDependent) {
      let categoryProds = [];
      let offerProds = [];

      while (filteredProducts?.length > 0) {
        // category products
        let i = 0;
        while (true) {
          const productID = filteredProducts[i]?.product?.id;
          const productBarcode = filteredProducts[i]?.barcode;

          if (isCategoryQuantityFull) break;
          if(filteredProducts.length === 0) break;

          let UnitsNeeded = offerFilters.categoriesQuantity - allCategoriesQtys;
          let UnitsToPush =
            filteredProducts[i].remainingQty >= UnitsNeeded
              ? UnitsNeeded
              : filteredProducts[i].remainingQty;

          categoryProds.push({
            id: productID,
            qty: UnitsToPush,
            barcode: productBarcode,
            product: filteredProducts[i]?.product,
            selectedButtonId: filteredProducts[i]?.selectedButtonId,
          });

          allCategoriesQtys += UnitsToPush;
          if (allCategoriesQtys == offerFilters.categoriesQuantity)
            isCategoryQuantityFull = true;

          filteredProducts[i].remainingQty -= UnitsToPush;
          if (filteredProducts[i].remainingQty == 0) {
            filteredProducts.splice(i, 1);
          } else {
            if (filteredProducts?.length > 0) i++;
          }
        }

        i = filteredProducts.length - 1;
        // offer products
        while (true) {
          const productID = filteredProducts[i]?.product?.id;
          const productBarcode = filteredProducts[i]?.barcode;

          if (isOfferCategoryQuantityFull) break;
          if(filteredProducts.length === 0) break;

          let UnitsNeeded =
            offerFilters.offerCategoriesQuantity - allOfferCategoriesQtys;
          let UnitsToPush =
            filteredProducts[i].remainingQty >= UnitsNeeded
              ? UnitsNeeded
              : filteredProducts[i].remainingQty;

          offerProds.push({
            id: productID,
            qty: UnitsToPush,
            barcode: productBarcode,
            product: filteredProducts[i]?.product,
            selectedButtonId: filteredProducts[i]?.selectedButtonId,
          });

          allOfferCategoriesQtys += UnitsToPush;
          if (allOfferCategoriesQtys == offerFilters.offerCategoriesQuantity)
            isOfferCategoryQuantityFull = true;

          filteredProducts[i].remainingQty -= UnitsToPush;
          if (filteredProducts[i].remainingQty == 0) {
            filteredProducts.splice(i, 1);
          }

          if (filteredProducts?.length > 0 && i > 0) i--;
        }

        isCategoryQuantityFull = false;
        allCategoriesQtys = 0;
        isOfferCategoryQuantityFull = false;
        allOfferCategoriesQtys = 0;
      }

      offerProds.forEach((prod) => {
        const isProdAlreadyAdded = offerProducts?.find(
          (offerProd) => offerProd?.barcode === prod?.barcode && offerProd?.selectedButtonId === prod?.selectedButtonId
        );
        if (isProdAlreadyAdded) {
          isProdAlreadyAdded.quantity += prod?.qty;
        } else {
          offerProducts.push({
            productID: prod?.id,
            barcode: prod?.barcode,
            selectedButtonId: prod?.selectedButtonId,
            quantity: prod?.qty,
          });
        }
      });
      categoryProds.forEach((prod) => {
        const isProdAlreadyAdded = categoryProducts?.find(
          (categoryProd) => categoryProd?.barcode === prod?.barcode && categoryProd?.selectedButtonId === prod?.selectedButtonId
        );
        if (isProdAlreadyAdded) {
          isProdAlreadyAdded.quantity += prod?.qty;
        } else {
          categoryProducts.push({
            productID: prod?.id,
            barcode: prod?.barcode,
            selectedButtonId: prod?.selectedButtonId,
            quantity: prod?.qty,
          });
        }
      });
    }

    if (isCategoryDependent && !isOfferCategoryDependent) {
      let categoryProds = [];
      let offerProds = [];

      for (let i = 0; i < filteredProducts.length; i++) {
        const productID = filteredProducts[i]?.product?.id;
        const productBarcode = filteredProducts[i]?.barcode;
        const productCategories = filteredProducts[
          i
        ]?.product?.attributes?.Categories?.data?.map((cat) => cat?.id);
        let isCategoryMatch = false;

        for (const category of productCategories) {
          const isCategoryExcluded = offerFilters?.excludedCategories?.find(
            (cat) => cat === category
          );
          if (isCategoryExcluded) continue;

          const isCategoryIncluded = offerFilters?.categories?.find(
            (cat) => cat === category
          );

          if (!isCategoryIncluded) continue;

          isCategoryMatch = true;
          break;
        }

        if (!isCategoryMatch) continue;
        if (filteredProducts[i].remainingQty <= 0) continue;

        let UnitsNeeded = offerFilters.categoriesQuantity - allCategoriesQtys;
        let UnitsToPush =
          filteredProducts[i].remainingQty >= UnitsNeeded
            ? UnitsNeeded
            : filteredProducts[i].remainingQty;

        categoryProds.push({
          id: productID,
          qty: UnitsToPush,
          barcode: productBarcode,
          product: filteredProducts[i]?.product,
        });

        allCategoriesQtys += UnitsToPush;
        if (allCategoriesQtys == offerFilters.categoriesQuantity) {
          isCategoryQuantityFull = true;
        }

        filteredProducts[i].remainingQty -= UnitsToPush;

        if (!isCategoryQuantityFull) continue;

        for (let j = filteredProducts.length - 1; j >= 0; j--) {
          const productID = filteredProducts[j]?.product?.id;
          const productBarcode = filteredProducts[j]?.barcode;

          if (isOfferCategoryQuantityFull) break;
          if (filteredProducts[j].remainingQty <= 0) continue;

          let UnitsNeeded =
            offerFilters.offerCategoriesQuantity - allOfferCategoriesQtys;
          let UnitsToPush =
            filteredProducts[j].remainingQty >= UnitsNeeded
              ? UnitsNeeded
              : filteredProducts[j].remainingQty;

          offerProds.push({
            id: productID,
            qty: UnitsToPush,
            barcode: productBarcode,
            product: filteredProducts[j]?.product,
            selectedButtonId: filteredProducts[j]?.selectedButtonId,
          });

          allOfferCategoriesQtys += UnitsToPush;
          if (allOfferCategoriesQtys == offerFilters.offerCategoriesQuantity) {
            isOfferCategoryQuantityFull = true;
          }

          filteredProducts[j].remainingQty -= UnitsToPush;
        }

        isCategoryQuantityFull = false;
        allCategoriesQtys = 0;
        isOfferCategoryQuantityFull = false;
        allOfferCategoriesQtys = 0;
      }

      isCategoryQuantityFull = false;
      allCategoriesQtys = 0;
      isOfferCategoryQuantityFull = false;
      allOfferCategoriesQtys = 0;

      offerProds.forEach((prod) => {
        const isProdAlreadyAdded = offerProducts?.find(
          (offerProd) => offerProd?.barcode === prod?.barcode && offerProd?.selectedButtonId === prod?.selectedButtonId
        );
        if (isProdAlreadyAdded) {
          isProdAlreadyAdded.quantity += prod?.qty;
        } else {
          offerProducts.push({
            productID: prod?.id,
            barcode: prod?.barcode,
            selectedButtonId: prod?.selectedButtonId,
            quantity: prod?.qty,
          });
        }
      });
      categoryProds.forEach((prod) => {
        const isProdAlreadyAdded = categoryProducts?.find(
          (categoryProd) => categoryProd?.barcode === prod?.barcode && categoryProd?.selectedButtonId === prod?.selectedButtonId
        );
        if (isProdAlreadyAdded) {
          isProdAlreadyAdded.quantity += prod?.qty;
        } else {
          categoryProducts.push({
            productID: prod?.id,
            barcode: prod?.barcode,
            selectedButtonId: prod?.selectedButtonId,
            quantity: prod?.qty,
          });
        }
      });
    }

    if (!isCategoryDependent && isOfferCategoryDependent) {
      let categoryProds = [];
      let offerProds = [];

      for (let j = filteredProducts.length - 1; j >= 0; j--) {
        const productID = filteredProducts[j]?.product?.id;
        const productBarcode = filteredProducts[j]?.barcode;
        const productCategories = filteredProducts[
          j
        ]?.product?.attributes?.Categories?.data?.map((cat) => cat?.id);
        let isOfferCategoryMatch = false;

        for (const category of productCategories) {
          const isCategoryExcluded = offerFilters?.excludedCategories?.find(
            (cat) => cat === category
          );
          if (isCategoryExcluded) continue;

          const isCategoryIncluded = offerFilters?.offerCategories?.find(
            (cat) => cat === category
          );

          if (!isCategoryIncluded) continue;

          isOfferCategoryMatch = true;
        }

        if (!isOfferCategoryMatch) continue;
        if (filteredProducts[j].remainingQty <= 0) continue;

        let UnitsNeeded =
          offerFilters.offerCategoriesQuantity - allOfferCategoriesQtys;
        let UnitsToPush =
          filteredProducts[j].remainingQty >= UnitsNeeded
            ? UnitsNeeded
            : filteredProducts[j].remainingQty;

        offerProds.push({
          id: productID,
          qty: UnitsToPush,
          barcode: productBarcode,
          product: filteredProducts[j]?.product,
        });

        allOfferCategoriesQtys += UnitsToPush;
        if (allOfferCategoriesQtys == offerFilters.offerCategoriesQuantity) {
          isOfferCategoryQuantityFull = true;
        }

        filteredProducts[j].remainingQty -= UnitsToPush;
        if (!isOfferCategoryQuantityFull) continue;

        for (let i = 0; i < filteredProducts.length; i++) {
          const productID = filteredProducts[i]?.product?.id;
          const productBarcode = filteredProducts[i]?.barcode;

          if (isCategoryQuantityFull) break;
          if (filteredProducts[i].remainingQty <= 0) continue;

          let UnitsNeeded = offerFilters.categoriesQuantity - allCategoriesQtys;
          let UnitsToPush =
            filteredProducts[i].remainingQty >= UnitsNeeded
              ? UnitsNeeded
              : filteredProducts[i].remainingQty;

          categoryProds.push({
            id: productID,
            qty: UnitsToPush,
            barcode: productBarcode,
            product: filteredProducts[i]?.product,
            selectedButtonId: filteredProducts[i]?.selectedButtonId,
          });

          allCategoriesQtys += UnitsToPush;
          if (allCategoriesQtys == offerFilters.categoriesQuantity) {
            isCategoryQuantityFull = true;
          }

          filteredProducts[i].remainingQty -= UnitsToPush;
        }

        isCategoryQuantityFull = false;
        allCategoriesQtys = 0;
        isOfferCategoryQuantityFull = false;
        allOfferCategoriesQtys = 0;
      }

      offerProds.forEach((prod) => {
        const isProdAlreadyAdded = offerProducts?.find(
          (offerProd) => offerProd?.barcode === prod?.barcode && offerProd?.selectedButtonId === prod?.selectedButtonId
        );
        if (isProdAlreadyAdded) {
          isProdAlreadyAdded.quantity += prod?.qty;
        } else {
          offerProducts.push({
            productID: prod?.id,
            barcode: prod?.barcode,
            selectedButtonId: prod?.selectedButtonId,
            quantity: prod?.qty,
          });
        }
      });
      categoryProds.forEach((prod) => {
        const isProdAlreadyAdded = categoryProducts?.find(
          (categoryProd) => categoryProd?.barcode === prod?.barcode && categoryProd?.selectedButtonId === prod?.selectedButtonId
        );
        if (isProdAlreadyAdded) {
          isProdAlreadyAdded.quantity += prod?.qty;
        } else {
          categoryProducts.push({
            productID: prod?.id,
            barcode: prod?.barcode,
            selectedButtonId: prod?.selectedButtonId,
            quantity: prod?.qty,
          });
        }
      });
    }

    if (isCategoryDependent && isOfferCategoryDependent) {
      let categoryProds = [];
      let offerProds = [];

      while (filteredProducts?.length > 0) {
        // category products
        for (let i = 0; i < filteredProducts.length; i++) {
          const productID = filteredProducts[i]?.product?.id;
          const productBarcode = filteredProducts[i]?.barcode;
          const productCategories = filteredProducts[
            i
          ]?.product?.attributes?.Categories?.data?.map((cat) => cat?.id);
          let isCategoryMatch = false;

          if (isCategoryQuantityFull) break;

          for (const category of productCategories) {
            const isCategoryExcluded = offerFilters?.excludedCategories?.find(
              (cat) => cat === category
            );
            if (isCategoryExcluded) continue;

            const isCategoryIncluded = offerFilters?.categories?.find(
              (cat) => cat === category
            );

            if (!isCategoryIncluded) continue;

            isCategoryMatch = true;
            break;
          }

          if (!isCategoryMatch) continue;
          if (filteredProducts[i].remainingQty <= 0) continue;

          let UnitsNeeded = offerFilters.categoriesQuantity - allCategoriesQtys;
          let UnitsToPush =
            filteredProducts[i].remainingQty >= UnitsNeeded
              ? UnitsNeeded
              : filteredProducts[i].remainingQty;

          categoryProds.push({
            id: productID,
            qty: UnitsToPush,
            barcode: productBarcode,
            product: filteredProducts[i]?.product,
            selectedButtonId: filteredProducts[i]?.selectedButtonId,
          });

          allCategoriesQtys += UnitsToPush;
          if (allCategoriesQtys == offerFilters.categoriesQuantity) {
            isCategoryQuantityFull = true;
          }

          filteredProducts[i].remainingQty -= UnitsToPush;
          if (filteredProducts[i].remainingQty == 0)
            filteredProducts.splice(i, 1);
        }

        if (!isCategoryQuantityFull) break;

        // offer products
        for (let i = filteredProducts.length - 1; i >= 0; i--) {
          const productID = filteredProducts[i]?.product?.id;
          const productBarcode = filteredProducts[i]?.barcode;
          const productCategories = filteredProducts[
            i
          ]?.product?.attributes?.Categories?.data?.map((cat) => cat?.id);
          let isOfferCategoryMatch = false;

          if (isOfferCategoryQuantityFull) break;

          for (const category of productCategories) {
            const isCategoryExcluded = offerFilters?.excludedCategories?.find(
              (cat) => cat === category
            );
            if (isCategoryExcluded) continue;

            const isCategoryIncluded = offerFilters?.offerCategories?.find(
              (cat) => cat === category
            );

            if (!isCategoryIncluded) continue;

            isOfferCategoryMatch = true;
          }

          if (!isOfferCategoryMatch) continue;
          if (filteredProducts[i].remainingQty <= 0) continue;

          let UnitsNeeded =
            offerFilters.offerCategoriesQuantity - allOfferCategoriesQtys;
          let UnitsToPush =
            filteredProducts[i].remainingQty >= UnitsNeeded
              ? UnitsNeeded
              : filteredProducts[i].remainingQty;

          offerProds.push({
            id: productID,
            qty: UnitsToPush,
            barcode: productBarcode,
            product: filteredProducts[i]?.product,
            selectedButtonId: filteredProducts[i]?.selectedButtonId,
          });

          allOfferCategoriesQtys += UnitsToPush;
          if (allOfferCategoriesQtys == offerFilters.offerCategoriesQuantity) {
            isOfferCategoryQuantityFull = true;
          }

          filteredProducts[i].remainingQty -= UnitsToPush;
          if (filteredProducts[i].remainingQty == 0)
            filteredProducts.splice(i, 1);
        }

        if (!isOfferCategoryQuantityFull) break;

        isCategoryQuantityFull = false;
        allCategoriesQtys = 0;
        isOfferCategoryQuantityFull = false;
        allOfferCategoriesQtys = 0;
      }

      offerProds.forEach((prod) => {
        const isProdAlreadyAdded = offerProducts?.find(
          (offerProd) => offerProd?.barcode === prod?.barcode && offerProd?.selectedButtonId === prod?.selectedButtonId
        );
        if (isProdAlreadyAdded) {
          isProdAlreadyAdded.quantity += prod?.qty;
        } else {
          offerProducts.push({
            productID: prod?.id,
            barcode: prod?.barcode,
            selectedButtonId: prod?.selectedButtonId,
            quantity: prod?.qty,
          });
        }
      });
      categoryProds.forEach((prod) => {
        const isProdAlreadyAdded = categoryProducts?.find(
          (categoryProd) => categoryProd?.barcode === prod?.barcode && categoryProd?.selectedButtonId === prod?.selectedButtonId
        );
        if (isProdAlreadyAdded) {
          isProdAlreadyAdded.quantity += prod?.qty;
        } else {
          categoryProducts.push({
            productID: prod?.id,
            barcode: prod?.barcode,
            selectedButtonId: prod?.selectedButtonId,
            quantity: prod?.qty,
          });
        }
      });
    }

    let categoriesMissing = false;
    let offerCategoriesMissing = false;
    let categoriesQuantities = 0;
    let offerCategoriesQuantities = 0;

    categoryProducts.forEach((prod) => {
      categoriesQuantities += prod?.quantity;
    });
    offerProducts.forEach((prod) => {
      offerCategoriesQuantities += prod?.quantity;
    });

    if (
      categoryProducts?.length === 0 ||
      categoriesQuantities < offerFilters.categoriesQuantity
    ) {
      isApplicable = false;
      categoriesMissing = true;
    }

    if (
      offerProducts?.length === 0 ||
      offerCategoriesQuantities < offerFilters.offerCategoriesQuantity
    ) {
      isApplicable = false;
      offerCategoriesMissing = true;
    }

    if (isApplicable) {
      appliedOffer = offer;
      // CheapestProducts = getNCheapestProducts(
      //   offer?.attributes?.OfferQuantity,
      //   offerProducts
      // );
      CheapestProducts = [...offerProducts];
      break;
    }

    if (!isApplicable) {
      // add one more product
      if (!isOfferCategoryDependent && !isCategoryDependent) {
        let acc = 0;
        categoryProducts.forEach((prod) => {
          acc += prod.quantity;
        });
        offerProducts.forEach((prod) => {
          acc += prod.quantity;
        });

        if (
          acc + 1 ==
          offerFilters.offerCategoriesQuantity + offerFilters.categoriesQuantity
        ) {
          offerTip = `You can add one more product to get `;
          if (offer?.attributes?.DiscountType === "Percentage") {
            offerTip += `a ${offer?.attributes?.DiscountValue}% offer`;
          } else {
            offerTip += `${offer?.attributes?.DiscountValue} AED deduction`;
          }
          offerTip += ` on ${offerFilters.offerCategoriesQuantity} ${
            offerFilters.offerCategoriesQuantity > 1 ? "items" : "item"
          } in your cart!`;
        }
      }

      // add one category product
      if (categoriesMissing && !offerCategoriesMissing) {
        let acc = 0;
        categoryProducts.forEach((prod) => {
          acc += prod.quantity;
        });
        offerProducts.forEach((prod) => {
          acc += prod.quantity;
        });

        if (
          acc + 1 ==
          offerFilters.offerCategoriesQuantity + offerFilters.categoriesQuantity
        ) {
          const missingCategoriesNames = offer?.attributes?.Categories?.data;
          if (!isCategoryDependent) {
            offerTip = `You can add one more product to get `;
          } else {
            if (missingCategoriesNames?.length > 1) {
              offerTip = `You can add one more product that has one of the categories ${missingCategoriesNames
                ?.map((cat) => cat?.attributes?.Title)
                ?.join(", ")}, to get `;
            } else {
              offerTip = `You can add one more product that has the category ${missingCategoriesNames[0]?.attributes?.Title} to get `;
            }
          }
          if (offer?.attributes?.DiscountType === "Percentage") {
            offerTip += `a ${offer?.attributes?.DiscountValue}% offer`;
          } else {
            offerTip += `${offer?.attributes?.DiscountValue} AED deduction`;
          }
          offerTip += ` on ${offerFilters.offerCategoriesQuantity} ${
            offerFilters.offerCategoriesQuantity > 1 ? "items" : "item"
          } in your cart!`;
        }
      }

      // add one offer product
      if (offerCategoriesMissing && !categoriesMissing) {
        let isOneProductMissing = false;
        let missingCategory = 0;
        for (const category of matchingObject.offerCategoriesQuantity) {
          if (category.qty + 1 === offerFilters.offerCategoriesQuantity) {
            if (!isOneProductMissing) {
              isOneProductMissing = true;
              missingCategory = category;
            } else {
              isOneProductMissing = false;
              break;
            }
          }
        }
        if (isOneProductMissing) {
          const missingCategoryName =
            offer?.attributes?.OfferCategories?.data?.find(
              (cat) => cat?.id === missingCategory?.id
            );
          if (missingCategoryName) {
            offerTip = `You can get `;
            if (offer?.attributes?.DiscountType === "Percentage") {
              offerTip += `a ${offer?.attributes?.DiscountValue}% offer `;
            } else {
              offerTip += `${offer?.attributes?.DiscountValue} AED deduction `;
            }
            offerTip += `if you add one more product that has the category ${missingCategoryName?.attributes?.Title}`;
          }
        }
      }
    }
  }
  return {
    offer: appliedOffer,
    tip: offerTip,
    offerAppliedProducts: CheapestProducts,
  };
};

// function generateCombinations(products) {
//   const expanded = [];

//   for (const product of products) {
//     for (let i = 0; i < product.qty; i++) {
//       const clone = JSON.parse(JSON.stringify(product));
//       clone.qty = 1;
//       clone.remainingQty = 1;
//       expanded.push(clone);
//     }
//   }

//   const results = [];

//   const combine = (prefix, rest, start = 0) => {
//     for (let i = start; i < rest.length; i++) {
//       const newCombo = [...prefix, rest[i]];
//       results.push(newCombo);
//       combine(newCombo, rest, i + 1);
//     }
//   };

//   combine([], expanded);
//   return results;
// }

function generateCombinations(products) {
  // expand products into unit-level items
  const units = [];
  for (const product of products) {
    for (let i = 0; i < product.qty; i++) {
      units.push({
        ...product,
        qty: 1,
        remainingQty: 1,
      });
    }
  }

  const results = [];

  // generate all unique combinations (subsets)
  const combine = (start, combo) => {
    if (combo.length > 0) {
      results.push([...combo]);
    }
    for (let i = start; i < units.length; i++) {
      // skip duplicates of identical items at the same tree depth
      if (i > start && isSameUnit(units[i], units[i - 1])) continue;

      combo.push(units[i]);
      combine(i + 1, combo);
      combo.pop();
    }
  };

  // sort units to ensure similar ones are grouped (for deduplication)
  units.sort((a, b) => {
    const keyA = `${a.barcode}-${a.selectedVariantId}`;
    const keyB = `${b.barcode}-${b.selectedVariantId}`;
    return keyA.localeCompare(keyB);
  });

  combine(0, []);
  return results;
}

function isSameUnit(a, b) {
  return a.barcode === b.barcode && a.selectedVariantId === b.selectedVariantId;
}

export const applyClearances = (products, unsortedClearances) => {
  if (products?.length <= 0 || unsortedClearances?.length <= 0) {
    return [];
  }

  const clearances = [...unsortedClearances].sort(
    (a, b) => a?.attributes?.Priority - b.attributes?.Priority
  );

  const results = [];
  const prods = structuredClone(products);

  for (const clearance of clearances) {
    const today = normalizeDate(new Date());
    const fromDate = normalizeDate(clearance?.attributes?.FromDate, true);
    const toDate = normalizeDate(clearance?.attributes?.ToDate, true);

    if (toDate < today || fromDate > today) {
      continue;
    }

    if (
      !clearance?.attributes?.Price ||
      clearance?.attributes?.Products?.length === 0
    ) {
      continue;
    }

    const sortedProductsByPrice = prods?.toSorted((a, b) => {
      const aPrice =
        a?.product?.attributes?.discountedPrice ??
        a?.product?.attributes?.Price;
      const bPrice =
        b?.product?.attributes?.discountedPrice ??
        b?.product?.attributes?.Price;

      return aPrice - bPrice;
    });

    const remainingProducts = sortedProductsByPrice.map((product) => {
      if (product) {
        return {
          ...product,
          remainingQty: product?.qty,
        };
      }
    });

    let prodsCombinations = generateCombinations(remainingProducts);

    // filtering based on total price of products
    const clearanceFilteredSets = [];
    for (const prodSet of prodsCombinations) {
      let totalPrice = 0;

      for (const prod of prodSet) {
        const prodPrice =
          prod?.product?.attributes?.discountedPrice ??
          prod?.product?.attributes?.Price * prod?.qty;
        totalPrice += prodPrice;
      }

      if (totalPrice > clearance?.attributes?.Price) {
        clearanceFilteredSets?.push(prodSet);
      }
    }

    // sorting by price
    const clearanceSortedSets = clearanceFilteredSets?.toSorted((a, b) => {
      let aTotalPrice = 0;
      let bTotalPrice = 0;

      for (const prod of a) {
        const prodPrice =
          prod?.product?.attributes?.discountedPrice ??
          prod?.product?.attributes?.Price * prod?.qty;
        aTotalPrice += prodPrice;
      }

      for (const prod of b) {
        const prodPrice =
          prod?.product?.attributes?.discountedPrice ??
          prod?.product?.attributes?.Price * prod?.qty;
        bTotalPrice += prodPrice;
      }

      return aTotalPrice - bTotalPrice;
    });

    let clearanceApplied = false;
    const clearanceAppliedProducts = [];
    let clearanceEntriesTotalQty = 0;
    for (const entry of clearance?.attributes?.Products) {
      clearanceEntriesTotalQty += entry?.Quantity;
    }

    // filtering all products combinations based on clearance rules
    for (const combination of clearanceSortedSets) {
      const clearanceSets = [];
      const newProducts = [];
      const sortedCombination = combination?.toSorted((a, b) => {
        const aPrice =
          a?.product?.attributes?.discountedPrice ??
          a?.product?.attributes?.Price;
        const bPrice =
          b?.product?.attributes?.discountedPrice ??
          b?.product?.attributes?.Price;

        return aPrice - bPrice;
      });
      let isApplicable = false;
      let areEntriesFulfilled = false;
      let prodsQtys = 0;
      for (const prod of combination) {
        newProducts.push(JSON.parse(JSON.stringify(prod)));
        prodsQtys += prod?.remainingQty;
      }

      while (prodsQtys > 0) {
        for (const entry of clearance?.attributes?.Products) {
          const isCategoriesDependent = entry?.Categories?.data?.length > 0;
          const isProductsDependent = entry?.Products?.data?.length > 0;
          const quantity = entry?.Quantity;
          const excludedProducts = entry?.xProduct?.data;

          let quantityCounter = 0;
          let entryFulfilled = false;

          for (const product of newProducts) {
            if (product.remainingQty <= 0) continue;
            let isMatch = false;

            if (isCategoriesDependent) {
              for (const category of entry?.Categories?.data) {
                const foundedCategory =
                  product?.product?.attributes?.Categories?.data?.find(
                    (cat) => cat?.id === category?.id
                  );
                if (foundedCategory) {
                  isMatch = true;
                  break;
                }
              }
            }

            if (isProductsDependent) {
              for (const entryProduct of entry?.Products?.data) {
                if (product?.product?.id === entryProduct?.id) {
                  isMatch = true;
                  break;
                }
              }
            }

            if (isMatch) {
              const isProductExcluded = excludedProducts?.find(
                (xProd) => xProd?.id === product?.product?.id
              );
              if (isProductExcluded) isMatch = false;
            }

            if (!isMatch) continue;

            let UnitsNeeded = quantity - quantityCounter;
            let UnitsToPush =
              product.remainingQty >= UnitsNeeded
                ? UnitsNeeded
                : product.remainingQty;

            clearanceSets.push({
              id: product?.product?.id,
              qty: UnitsToPush,
              barcode: product?.barcode,
              product: product,
            });

            quantityCounter += UnitsToPush;
            prodsQtys -= UnitsToPush;
            product.remainingQty -= UnitsToPush;

            if (quantityCounter >= quantity) {
              entryFulfilled = true;
              break;
            }
          }

          if (entryFulfilled) areEntriesFulfilled = true;
          if (!entryFulfilled) {
            areEntriesFulfilled = false;
            break;
          }
        }

        if (!areEntriesFulfilled) break;
      }

      if (areEntriesFulfilled) {
        isApplicable = true;
        const temp = [];
        clearanceSets.forEach((prod) => {
          const isProdAlreadyAdded = temp?.find(
            (clearanceProd) => clearanceProd?.barcode === prod?.barcode
          );
          if (isProdAlreadyAdded) {
            isProdAlreadyAdded.quantity += prod?.qty;
          } else {
            temp.push({
              productID: prod?.id,
              barcode: prod?.barcode,
              quantity: prod?.qty,
              product: prod?.product,
            });
          }
        });
        clearanceAppliedProducts?.push(temp);
        clearanceSets.length = 0;
      }
      if (isApplicable) clearanceApplied = true;
    }

    if (clearanceApplied) {
      // choosing combinations, filtering by price, and deleting duplicates
      const filteredByPrice = [];
      for (const prodSet of clearanceAppliedProducts) {
        let totalPrice = 0;

        for (const prod of prodSet) {
          const prodPrice =
            prod?.product?.product?.attributes?.discountedPrice ??
            prod?.product?.product?.attributes?.Price * prod?.quantity;
          totalPrice += prodPrice;
        }

        if (totalPrice > clearance?.attributes?.Price) {
          filteredByPrice?.push(prodSet);
        }
      }

      const sortedByPrice = filteredByPrice?.toSorted((a, b) => {
        let aTotalPrice = 0;
        let bTotalPrice = 0;

        for (const prod of a) {
          const prodPrice =
            prod?.product?.product?.attributes?.discountedPrice ??
            prod?.product?.product?.attributes?.Price * prod?.qty;
          aTotalPrice += prodPrice;
        }

        for (const prod of b) {
          const prodPrice =
            prod?.product?.product?.attributes?.discountedPrice ??
            prod?.product?.product?.attributes?.Price * prod?.qty;
          bTotalPrice += prodPrice;
        }

        return aTotalPrice - bTotalPrice;
      });

      const selectedSets = [];
      for (let i = 0; i < sortedByPrice?.length; i++) {
        let prodSet = sortedByPrice[i];
        prodSet = prodSet.filter((prod) => prod?.quantity > 0);

        let isValid = true;
        let setQtyCounter = 0;
        for (const prod of prodSet) {
          setQtyCounter += prod?.quantity;
        }

        if (clearanceEntriesTotalQty > setQtyCounter) isValid = false;

        if (isValid) {
          selectedSets?.push(prodSet);
          for (let j = i + 1; j < sortedByPrice?.length; j++) {
            const nextProdSet = sortedByPrice[j];
            for (const prod of prodSet) {
              const prodMatch = nextProdSet?.find(
                (p) => p?.barcode === prod?.barcode
              );
              if (prodMatch) {
                prodMatch.quantity -= prod?.quantity;
              }
            }
          }
        } else {
          continue;
        }
      }

      if (selectedSets?.length > 0) {
        for (const selectedSet of selectedSets) {
          for (const prod of selectedSet) {
            const prodMatch = prods?.find((p) => p?.barcode === prod?.barcode);
            if (prodMatch) {
              prodMatch.qty -= prod?.quantity;
            }
          }
        }
      }

      results.push({
        clearance: clearance,
        clearanceProducts: selectedSets,
      });
    }
  }

  return results;
};

export const checkCustomShippingRates = (
  customRatesArr,
  subtotal,
  shippingProvider,
  shippingCountry
) => {
  if (
    customRatesArr?.length === 0 ||
    !subtotal ||
    !shippingProvider ||
    !shippingCountry
  ) {
    return { hasCustomShippingRate: false, customShippingRate: 0 };
  }

  for (const customRate of customRatesArr) {
    const rateMinTotal = customRate?.attributes?.MinOrderTotal;
    const rateCountries = customRate?.attributes?.ShippingCountries?.data;
    const rateShippingProvider =
      customRate?.attributes?.ShippingProvider?.data?.attributes?.Name;
    const rateAmount = customRate?.attributes?.Amount;

    const today = normalizeDate(new Date());
    const fromDate = normalizeDate(customRate?.attributes?.StartDate, true);
    const toDate = normalizeDate(customRate?.attributes?.endDate, true);

    if (toDate < today || fromDate > today) {
      continue;
    }

    if (!rateMinTotal && rateCountries?.length === 0 && !rateShippingProvider)
      continue;

    if (rateMinTotal) {
      if (parseFloat(rateMinTotal) > parseFloat(subtotal)) continue;
    }

    if (rateShippingProvider) {
      if (rateShippingProvider !== shippingProvider) continue;
    }

    if (rateCountries?.length > 0) {
      const countryMatch = rateCountries?.find((c) => c?.id == shippingCountry);
      if (!countryMatch) continue;
    }

    return { hasCustomShippingRate: true, customShippingRate: rateAmount };
  }

  return { hasCustomShippingRate: false, customShippingRate: 0 };
};

export const getColorAndSize = (
  product,
  barcode,
  selectedVariantId,
  locale = "en"
) => {
  const selectedVariant = product?.attributes?.Variants?.find(
    (variant) => variant?.id === selectedVariantId
  );

  if (selectedVariant) {
    let color = selectedVariant?.Color?.data?.attributes?.Name;
    if (locale != "en") {
      const localization =
        selectedVariant?.Color?.data?.attributes?.localizations?.data?.find(
          (local) => local?.attributes?.locale === locale
        );
      if (localization) {
        color = localization?.attributes?.Name;
      }
    }
    const selectedSize = selectedVariant?.Sizes?.find(
      (size) => size?.Barcode === barcode
    );
    if (selectedSize) {
      let size = selectedSize?.Size?.data?.attributes?.Name;
      if (locale != "en") {
        const localization =
          selectedSize?.Size?.data?.attributes?.localizations?.data?.find(
            (local) => local?.attributes?.locale === locale
          );
        if (localization) {
          size = localization?.attributes?.Name;
        }
      }
      return [color, size];
    }
  }

  return ["", ""];
};

export const getButton = (product, selectedButtonId, selectedVariantId, locale = "en") => {
  if (!selectedButtonId || !selectedVariantId) return "";
  const selectedVariant = product?.attributes?.Variants?.find(
    (variant) => variant?.id === selectedVariantId
  );
  const selectedButton = selectedVariant?.Button?.data?.find(
    (btn) => btn?.id === selectedButtonId
  );
  if (!selectedButton) return "";
  let buttonName = selectedButton?.attributes?.Name;
  if (locale !== "en") {
    const localization = selectedButton?.attributes?.localizations?.data?.find(
      (loc) => loc?.attributes?.locale === locale
    );
    if (localization) buttonName = localization?.attributes?.Name;
  }
  return buttonName;
};

export const generateUser = async (email, firstName, lastName, phone) => {
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let password = "";
  for (let i = 0; i < 8; i++) {
    const randomIndex = Math.floor(Math.random() * characters.length);
    password += characters[randomIndex];
  }

  try {
    const res = await makeRequest.post("/auth/local/register", {
      email: email,
      password: password,
      username: `${firstName + lastName}_${Date.now()}`,
      FirstName: firstName,
      LastName: lastName,
      PhoneNumber: phone,
    });

    return {
      error: false,
      data: res.data,
    };
  } catch (err) {
    return {
      error: true,
      data: err.response.data,
    };
  }
};

export const isPhoneValid = (phone) => {
  const phoneUtil = PhoneNumberUtil.getInstance();
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch (error) {
    return false;
  }
};

export const updateCountryFromPhoneNumber = (phoneNumber, setCountry) => {
  const phoneUtil = PhoneNumberUtil.getInstance();

  try {
    const parsedNumber = phoneUtil.parseAndKeepRawInput(phoneNumber);
    const countryCode = parsedNumber.getCountryCode();
    const regionCode = phoneUtil.getRegionCodeForCountryCode(countryCode);
    if (regionCode) {
      setCountry(regionCode.toLowerCase());
    }
  } catch (error) {
    console.warn("Error parsing phone number:", error);
  }
};

export const calculateWeight = (products, multiplyWithQty = true) => {
  let weight = 0;

  for (const prod of products) {
    const selectedVariantId = prod?.selectedVariantId;
    const barcode = prod?.barcode;

    const variant = prod?.product?.attributes?.Variants?.find(
      (variant) => variant?.id === selectedVariantId
    );
    if (variant) {
      const size = variant?.Sizes?.find((size) => size?.Barcode === barcode);
      if (size) {
        if (multiplyWithQty) weight += size?.Weight * prod?.qty;
        else weight += size?.Weight;
      }
    }
  }
  return parseFloat(weight?.toFixed(2));
};

export const getShippingRate = (
  shippingMethod,
  shippingRatesArr,
  country,
  city,
  area,
  products,
  // Optional Set of provider Names known to be active. When provided, a
  // provider missing from it is skipped even if its rate rows still match —
  // this is what drops a provider stopped in the admin after page load.
  // Omitted by every existing caller, which keeps their behaviour unchanged.
  activeProviderNames = null
) => {
  if (
    !shippingMethod ||
    products.length === 0 ||
    !country ||
    (!area && area != 0) ||
    !city ||
    shippingRatesArr.length === 0
  ) {
    return 0;
  }

  const weight = calculateWeight(products);
  for (const rate of shippingRatesArr) {
    const shippingProvider = rate?.attributes?.ShippingProvider;

    if (shippingProvider?.data?.attributes?.Name !== shippingMethod) continue;

    if (
      activeProviderNames &&
      !activeProviderNames.has(shippingProvider?.data?.attributes?.Name)
    ) {
      continue;
    }

    for (const rateArea of rate?.attributes?.Areas) {
      // A blank Country is always a data error, never "all countries" — treat
      // it as no match so the provider drops out of the list. City stays a
      // truthy guard because a Country-only row means "all cities".
      if (!rateArea?.Country?.data || rateArea.Country.data.id != country) {
        continue;
      }

      if (rateArea?.City?.data && rateArea.City.data.id != city) {
        continue;
      }

      if (rateArea?.Areas?.data?.length > 0) {
        const isValidArea = rateArea?.Areas?.data?.find(
          (Area) => Area?.id == area
        );

        if (!isValidArea) continue;
      }

      if (rateArea?.FromWeight > weight || rateArea?.ToWeight < weight)
        continue;

      if (rateArea?.FirstHalfKGPrice && rateArea?.RestHalfKGPrice) {
        const units = Math.ceil(weight / 0.5);
        const firstKG = parseFloat(rateArea?.FirstHalfKGPrice);
        const restKG = parseFloat(rateArea?.RestHalfKGPrice);
        const cost = firstKG + restKG * (units - 1);
        return cost;
      }

      return rateArea?.Fee;
    }
  }

  return 0;
};

// Re-checks the shipping providers against freshly fetched Strapi data, so a
// provider stopped or deleted in the admin while the page was open can no
// longer be used. The `_` cache-buster matches the refresh used for products,
// discount rules and offers at submit time.
//
// Returns a Set of the currently active provider Names, or null when the list
// cannot be fetched so the caller can fail open rather than block the order.
export const getFreshActiveShippingProviders = async () => {
  const providersParams = new URLSearchParams({
    populate: "*",
    locale: "en",
    "pagination[limit]": -1,
    "filters[Status][$eq]": true,
    _: Date.now(),
  });

  const res = await makeRequest.get(
    `/shipping-providers?${providersParams.toString()}`
  );
  const providers = res?.data?.data;
  if (!Array.isArray(providers)) return null;

  return new Set(
    providers.map((provider) => provider?.attributes?.Name).filter(Boolean)
  );
};

// Re-resolves the live rate for a statically priced provider, and returns the
// rate list it resolved against so the caller can rebuild the options list
// from the same data rather than the mount-time snapshot.
//
// `rate` is 0 when the provider is verifiably no longer offered for this
// destination/weight (rate rows removed, weight band changed, country/city/area
// row deleted), and null when the rate list itself could not be fetched. The
// caller must not treat null as unavailability, or a transient network failure
// would block an order for a method that is perfectly valid.
//
// Deliberately not used for DHL: that provider is priced live by the carrier,
// so availability is covered by the provider-list check alone and a carrier
// outage must never block an order.
export const fetchFreshShippingRates = () => {
  const ratesParams = new URLSearchParams({
    populate: "deep",
    "pagination[limit]": -1,
    _: Date.now(),
  });
  return makeRequest.get(`/shipping-rates?${ratesParams.toString()}`);
};
export const getFreshStaticShippingRate = async ({
  shippingMethod,
  country,
  city,
  area,
  products,
  activeProviderNames,
  ratesPromise,
}) => {
  if (!shippingMethod) return { rate: 0, rates: null };
  const res = await (ratesPromise || fetchFreshShippingRates());
  // const ratesParams = new URLSearchParams({
  //   populate: "deep",
  //   "pagination[limit]": -1,
  //   _: Date.now(),
  // });

  // const res = await makeRequest.get(
  //   `/shipping-rates?${ratesParams.toString()}`
  // );
  const rates = res?.data?.data;
  if (!Array.isArray(rates)) return { rate: null, rates: null };

  return {
    rate: getShippingRate(
      shippingMethod,
      rates,
      country,
      city,
      area,
      products,
      activeProviderNames,
      ratesPromise,
    ),
    rates,
  };
};

export const updateIsDeducted = async (orderID) => {
  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}orders/${orderID}`,
      {
        data: { QuantityDeducted: true },
      },
      {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_API_TOKEN}`,
        },
      }
    );
  } catch (error) {
    console.error("Quantity update failed:", error.response || error.message);
  }
};

export const updateSizeQuantity = async (productsIds, orderID) => {
  let products = [];
  const productsParams = new URLSearchParams({
    populate: "deep,4",
  });
  const validProductIds = productsIds.filter(p => p?.id != null);
  validProductIds.forEach((product, index) => {
    productsParams.append(`filters[id][$in][${index}]`, product.id);
  });

  try {
    const res = await makeRequest.get(`/products?${productsParams.toString()}`);
    products = res?.data?.data;
  } catch (err) {
    return {
      error: true,
      data: err?.response?.data || "An unknown error occurred",
    };
  }

  const organizedProductsByID = [];

  for (const productID of productsIds) {
    const product = products?.find((prod) => prod?.id == productID?.id);

    if (product) {
      const isProductAlreadyAdded = organizedProductsByID.find(
        (orgProd) => orgProd?.id == product?.id
      );
      if (isProductAlreadyAdded) {
        const isVariantAlreadyAdded = isProductAlreadyAdded?.variants?.find(
          (variant) => variant?.id == productID?.selectedVariantId
        );
        if (isVariantAlreadyAdded) {
          const isSizeAlreadyAdded = isVariantAlreadyAdded?.Sizes?.find(
            (size) => size?.Barcode == productID?.barcode
          );
          if (isSizeAlreadyAdded) {
            isSizeAlreadyAdded.Quantity -= parseInt(productID?.quantity);
          }
        }
      } else {
        const newVariants = [];
        for (const newVariant of product?.attributes?.Variants || []) {
          if (newVariant?.id != productID?.selectedVariantId) {
            newVariants.push({
              id: newVariant?.id,
              Sizes: (newVariant?.Sizes || []).map((size) => {
                return {
                  id: size?.id,
                  Quantity: size?.Quantity,
                  Barcode: size?.Barcode,
                };
              }),
            });
          } else {
            const newSizes = [];
            for (const newVariantSize of newVariant?.Sizes || []) {
              if (newVariantSize?.Barcode != productID?.barcode) {
                newSizes.push({
                  id: newVariantSize?.id,
                  Quantity: newVariantSize?.Quantity,
                  Barcode: newVariantSize?.Barcode,
                });
              } else {
                newSizes.push({
                  id: newVariantSize?.id,
                  Quantity:
                    parseInt(newVariantSize?.Quantity) -
                    parseInt(productID?.quantity),
                  Barcode: newVariantSize?.Barcode,
                });
              }
            }
            newVariants.push({
              id: newVariant?.id,
              Sizes: newSizes,
            });
          }
        }
        organizedProductsByID.push({
          id: product?.id,
          variants: newVariants,
        });
      }
    }
  }

  for (const organizedProduct of organizedProductsByID) {
    const payload = {
      data: {
        Variants: organizedProduct?.variants,
      },
    };

    try {
      const res = await makeRequest.put(
        `/products/${organizedProduct?.id}`,
        payload
      );
    } catch (err) {
      return {
        error: true,
        data: err.response?.data || "An unknown error occurred",
      };
    }
  }

  await updateIsDeducted(orderID);

  return {
    error: false,
    data: "An unknown error occurred",
  };
};

export const updateVoucher = async (voucher, amountUsed) => {
  const newAmount =
    parseFloat(voucher?.attributes?.Amount) - parseFloat(amountUsed);
  const newStatus = newAmount <= 0 ? "Used" : "Active";
  const payload = {
    data: {
      Status: newStatus,
      Amount: parseFloat(newAmount?.toFixed(2)),
    },
  };

  try {
    const res = await makeRequest.put(`/vouchers/${voucher?.id}`, payload);
  } catch (err) {
    return false;
  }

  return true;
};

export const subscribeToNewsletter = async (emailValue) => {
  const payload = {
    data: {
      Email: emailValue,
    },
  };

  try {
    const res = await makeRequest.post(`/newsletters`, payload);
  } catch (err) {
    return false;
  }

  return true;
};

export const generateInvoiceNumber = (number) => {
  const prefix = "NW";

  // return `${prefix}${number.toString().padStart(5, "0")}`;
  return `${prefix}${number.toString()}`;
};

export const generatePackageID = (offset) => {
  const prefix = "PKG";
  const timestamp = Date.now();

  return `${prefix}-${timestamp}${offset}`;
};

export const uploadBase64PdfToStrapi = async (base64Pdf, fileName) => {
  const base64WithPrefix = `data:application/pdf;base64,${base64Pdf}`;

  // Convert Base64 string to Blob
  const byteCharacters = atob(base64WithPrefix.split(",")[1]);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(null)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: "application/pdf" });

  // Create FormData
  const formData = new FormData();
  formData.append("files", blob, fileName);

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_API_TOKEN}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return [response?.data[0]?.id, false];
  } catch (error) {
    console.error("Upload error:", error.response || error.message);
    return [0, true];
  }
};

export const uploadPdfToStrapi = async (pdfBlob, fileName) => {
  const formData = new FormData();
  formData.append("files", pdfBlob, fileName);

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_API_TOKEN}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return [response?.data[0]?.id, false];
  } catch (error) {
    console.error("Upload error:", error.response || error.message);
    return [0, true];
  }
};

export const convertHTMLToPdf = async (url) => {
  try {
    const response = await axios.post(
      `https://api.html2pdf.app/v1/generate`,
      {
        html: url,
        apiKey:
          process.env.REACT_APP_HTML_TO_PDF_API_KEY ||
          "CLiaHCTTQe0RPcpyhNYa6fvL6gN87o7qJhQlhOydiGW3i5YTcGDAAVCZbQzi2iZ7",
        width: 1600,
        height: 600,
        marginRight: 12,
        marginLeft: 12,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        responseType: "blob",
      }
    );
    return response?.data;
  } catch (error) {
    console.error(
      "Converting HTML shipment label failed:",
      error.response || error.message
    );
  }
  return "";
};

export const isOutOfStock = (variants) => {
  if (!Array.isArray(variants) || variants.length === 0) return;

  let isAllOutOfStock = true;
  for (const variant of variants) {
    for (const size of variant?.Sizes || []) {
      if (size?.Quantity > 0) {
        if (variant?.Images?.data) {
          isAllOutOfStock = false;
          break;
        }
      }
    }
    if (!isAllOutOfStock) break;
  }

  if (isAllOutOfStock) {
    return true;
  }

  return false;
};

export const isSelectedVariantOutOfStock = (
  product,
  barcode,
  selectedVariantId
) => {
  const selectedVariant = product?.attributes?.Variants?.find(
    (variant) => variant?.id === selectedVariantId
  );

  if (selectedVariant) {
    const selectedSize = selectedVariant?.Sizes?.find(
      (size) => size?.Barcode === barcode
    );
    if (selectedSize) {
      return selectedSize?.Quantity <= 0;
    }
  }

  return true;
};

export const updateOrderWithInvoice = async (orderID) => {
  let invoice = generateInvoiceNumber(orderID);

  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}orders/${orderID}`,
      {
        data: { InvoiceNumber: invoice },
      },
      {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_API_TOKEN}`,
        },
      }
    );
  } catch (error) {
    console.error("Upload error:", error.response || error.message);
  }
};

export const getAddressFromCoords = async (lat, lon) => {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`
    );
    const data = await response.json();

    if (data && data.address) {
      const address = data?.display_name?.split(", ")?.toReversed();
      const newAddress = {
        Country: data.address.country || "",
        City:
          data.address.city ||
          data.address.state ||
          data.address.town ||
          data.address.village ||
          "",
        City2: data.address.state,
        Area: data.address.neighbourhood || "",
        Area2: data.address.suburb || "",
        Address1: data.address.neighbourhood || "",
        Address2: data.address.road || "",
        PostCode: data.address.postcode || "",
        DisplayName: data?.display_name,
        MapLink: `http://www.openstreetmap.org/?mlat=${data?.lat}&mlon=${data?.lon}&zoom=13`,
      };
      return newAddress;
    }
  } catch (error) {
    console.error("Error fetching address:", error);
  }
  return null;
};

export const getDHLShipmentEvents = async (trackingNumber) => {
  const payload = {
    tracking_id: `${trackingNumber}`,
  };

  try {
    const res = await makeRequest.post(`/dhl-track-shipment`, payload);
    return res?.data?.shipments[0]?.events;
  } catch (err) {
    return [];
  }

  return [];
};

export const uploadBlobsToStrapi = async (blobUrls) => {
  try {
    const formData = new FormData();
    const timestamp = Date.now();

    await Promise.all(
      blobUrls.map(async (blobUrl, index) => {
        const response = await fetch(blobUrl);
        const blob = await response.blob();
        formData.append(
          "files",
          blob,
          `exchange_request_file_${index + 1}_${timestamp}`
        );
      })
    );

    const uploadResponse = await axios.post(
      `${process.env.REACT_APP_API_URL}upload`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_API_TOKEN}`,
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return [uploadResponse?.data.map((file) => file.id), false];
  } catch (error) {
    console.error("Upload error:", error.response || error.message);
    return [[], true];
  }
};

export const updatePoints = async (points, userID) => {
  let userPoints = 0;

  if (!userID) return;
  if (!points) return;

  try {
    const res = await makeRequest.get(
      `/users?filters[id][$eq]=${userID}&populate=*`
    );
    if (res?.data) userPoints = res?.data[0]?.Points;
    else return;
  } catch (err) {
    return;
  }

  if (!userPoints) userPoints = 0;

  userPoints = parseFloat(userPoints) + parseFloat(points);

  const payload = {
    Points: userPoints?.toFixed(2),
  };

  try {
    const res = await makeRequest.put(`/users/${userID}`, payload);
  } catch (err) {
    return;
  }
};

export const getUserPoints = async (userID) => {
  try {
    const res = await makeRequest.get(
      `/users?filters[id][$eq]=${userID}&populate=*`
    );
    if (res?.data) return parseFloat(res?.data[0]?.Points);
    else return 0;
  } catch (err) {
    return 0;
  }
};

export const deleteUser = async (userID) => {
  if (!userID) return;

  try {
    const res = await makeRequest.delete(
      `/users/${userID}`
    );
    if (res?.data) return res?.data;
    else return 0;
  } catch (err) {
    return 0;
  }
}

export const getProductsQuantities = async (productsIDs) => {
  const params = new URLSearchParams({
    "fields[0]": "id",
    "populate[Variants][fields][0]": "id",
    "populate[Variants][populate][Sizes][fields][0]": "id",
    "populate[Variants][populate][Sizes][fields][1]": "Quantity",
    "populate[Variants][populate][Sizes][fields][2]": "barcode",
  });

  let index = 0;
  for (const id of productsIDs) {
    params.append(`filters[id][$in][${index}]`, id);
    index++;
  }

  try {
    const res = await makeRequest.get(`/products?${params.toString()}`);
    return res.data.data;
  } catch (err) {
    return [];
  }
};
